import { useState, useEffect, useRef, useCallback, useMemo, startTransition } from 'react';
import { TripSheetProps } from '../../../datastore/tripSheets';
import { ASNOccurrences, ManifestOccurrences, SSCCOccurrences } from '../utils/types';
import { captureError } from '../../../common/sentry';

const DEBOUNCE_MS = 300;

interface UseFilteredTripSheetsOptions {
  tripSheets: TripSheetProps[];
  tripSearch: string;
  departureAddressSearch: string;
  legDestinationSearch: string;
  selectedTripStatus: string;
  selectedLegStatus: string;
  manifestSearch: string;
  selectedManifestStatus: string;
  manifestTrailerIdSearch: string;
  asnSearch: string;
  selectedASNStatus: string;
  asnPoNumberSearch: string;
  ssccSearch: string;
  selectedSSCCStatus: string;
  ssccNicknameSearch: string;
  selectedLegDepartureDate: string;
  selectedLegArrivalDate: string;
  ssccStatusById: Record<string, string>;
  hideEmptyManifestTrips: boolean;
  showOnlyManifestDuplicates: boolean;
  showOnlySSCCDuplicates: boolean;
  showOnlyASNDuplicates: boolean;
  manifestOccurrences: ManifestOccurrences;
  ssccOccurrences: SSCCOccurrences;
  asnOccurrences: ASNOccurrences;
}

export function useFilteredTripSheets({
  tripSheets,
  tripSearch,
  departureAddressSearch,
  legDestinationSearch,
  selectedTripStatus,
  selectedLegStatus,
  manifestSearch,
  selectedManifestStatus,
  manifestTrailerIdSearch,
  asnSearch,
  selectedASNStatus,
  asnPoNumberSearch,
  ssccSearch,
  selectedSSCCStatus,
  ssccNicknameSearch,
  selectedLegDepartureDate,
  selectedLegArrivalDate,
  ssccStatusById,
  hideEmptyManifestTrips,
  showOnlyManifestDuplicates,
  showOnlySSCCDuplicates,
  showOnlyASNDuplicates,
  manifestOccurrences,
  ssccOccurrences,
  asnOccurrences
}: UseFilteredTripSheetsOptions): TripSheetProps[] {
  const tripSheetById = useMemo(() => new Map(tripSheets.map((t) => [t.id, t])), [tripSheets]);
  // Initialize with all IDs so content shows immediately before the worker responds
  const [filteredIds, setFilteredIds] = useState<string[]>(() => tripSheets.map((t) => t.id));
  const [workerReady, setWorkerReady] = useState(false);
  const workerRef = useRef<Worker | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const hasActiveFilters = useMemo(
    () =>
      Boolean(
        tripSearch.trim() ||
        departureAddressSearch.trim() ||
        legDestinationSearch.trim() ||
        selectedTripStatus ||
        selectedLegStatus ||
        manifestSearch.trim() ||
        selectedManifestStatus ||
        manifestTrailerIdSearch.trim() ||
        asnSearch.trim() ||
        selectedASNStatus ||
        asnPoNumberSearch.trim() ||
        ssccSearch.trim() ||
        selectedSSCCStatus ||
        ssccNicknameSearch.trim() ||
        selectedLegDepartureDate ||
        selectedLegArrivalDate ||
        hideEmptyManifestTrips ||
        showOnlyManifestDuplicates ||
        showOnlySSCCDuplicates ||
        showOnlyASNDuplicates
      ),
    [
      tripSearch,
      departureAddressSearch,
      legDestinationSearch,
      selectedTripStatus,
      selectedLegStatus,
      manifestSearch,
      selectedManifestStatus,
      manifestTrailerIdSearch,
      asnSearch,
      selectedASNStatus,
      asnPoNumberSearch,
      ssccSearch,
      selectedSSCCStatus,
      ssccNicknameSearch,
      selectedLegDepartureDate,
      selectedLegArrivalDate,
      hideEmptyManifestTrips,
      showOnlyManifestDuplicates,
      showOnlySSCCDuplicates,
      showOnlyASNDuplicates
    ]
  );

  const minimalTrips = useMemo(
    () =>
      tripSheets.map((trip) => ({
        id: trip.id,
        reference: trip.reference,
        legs: (trip.legs || []).map((leg) => ({
          status: leg.status,
          origin: {
            businessName: leg.origin?.businessName || '',
            addressLine1: leg.origin?.addressLine1 || '',
            addressLine2: leg.origin?.addressLine2 || '',
            suburb: leg.origin?.suburb || '',
            postCode: leg.origin?.postCode || '',
            state: leg.origin?.state || ''
          },
          departureDate:
            (leg.departureDate as any)?.toISOString && typeof (leg.departureDate as any).toISOString === 'function'
              ? (leg.departureDate as any).toISOString()
              : `${leg.departureDate || ''}`,
          arrivalDate:
            (leg.arrivalDate as any)?.toISOString && typeof (leg.arrivalDate as any).toISOString === 'function'
              ? (leg.arrivalDate as any).toISOString()
              : `${leg.arrivalDate || ''}`,
          destination: { businessName: leg.destination?.businessName || '' },
          manifests: (leg.manifests || []).map((manifest) => ({
            id: manifest.id,
            status: manifest.status,
            trailerId: manifest.trailerId || '',
            ssccs: (manifest.ssccs || []).map((sscc) => {
              return {
                ssccId: sscc.ssccId,
                status: sscc.status || ssccStatusById[sscc.ssccId] || '',
                nickname: sscc.nickname,
                packages: (sscc.packages || []).map((pkg) => {
                  return {
                    asn: pkg.asn,
                    id: pkg.id,
                    status: pkg.receivedStatus,
                    po: pkg.poNumber
                  };
                })
              };
            })
          }))
        }))
      })),
    [tripSheets, ssccStatusById]
  );

  const manifestDuplicateIds = useMemo(
    () =>
      showOnlyManifestDuplicates
        ? Array.from(manifestOccurrences.entries())
            .filter(([, occurrences]) => occurrences.length > 1)
            .map(([manifestId]) => manifestId)
        : [],
    [showOnlyManifestDuplicates, manifestOccurrences]
  );

  const ssccDuplicateIds = useMemo(
    () =>
      showOnlySSCCDuplicates
        ? Array.from(ssccOccurrences.entries())
            .filter(([, occurrences]) => occurrences.length > 1)
            .map(([ssccId]) => ssccId)
        : [],
    [showOnlySSCCDuplicates, ssccOccurrences]
  );

  const asnDuplicateKeys = useMemo(
    () =>
      showOnlyASNDuplicates
        ? Array.from(asnOccurrences.entries())
            .filter(([, occurrences]) => occurrences.length > 1)
            .map(([asnKey]) => asnKey)
        : [],
    [showOnlyASNDuplicates, asnOccurrences]
  );

  useEffect(() => {
    try {
      const worker = new Worker(new URL('../workers/filterTripSheetsWorker.ts', import.meta.url));
      worker.onmessage = (e: MessageEvent) => {
        startTransition(() => {
          setFilteredIds(e.data.ids);
        });
      };
      worker.onerror = (err) => {
        console.error('Filter trip sheets worker error:', err);
        captureError(err, 'adminPanel-useFilteredTripSheets-worker');
      };
      workerRef.current = worker;
      setWorkerReady(true);
    } catch (err) {
      console.error('Failed to create filter trip sheets worker:', err);
      captureError(err, 'adminPanel-useFilteredTripSheets-worker-create');
    }

    return () => {
      workerRef.current?.terminate();
      workerRef.current = null;
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const postToWorker = useCallback(() => {
    if (!workerRef.current) return;

    workerRef.current.postMessage({
      tripSheets: minimalTrips,
      tripSearch,
      departureAddressSearch,
      legDestinationSearch,
      selectedTripStatus,
      selectedLegStatus,
      manifestSearch,
      selectedManifestStatus,
      manifestTrailerIdSearch,
      asnSearch,
      selectedASNStatus,
      asnPoNumberSearch,
      ssccSearch,
      selectedSSCCStatus,
      ssccNicknameSearch,
      selectedLegDepartureDate,
      selectedLegArrivalDate,
      hideEmptyManifestTrips,
      showOnlyManifestDuplicates,
      showOnlySSCCDuplicates,
      showOnlyASNDuplicates,
      manifestDuplicateIds,
      ssccDuplicateIds,
      asnDuplicateKeys
    });
  }, [
    minimalTrips,
    tripSearch,
    departureAddressSearch,
    legDestinationSearch,
    selectedTripStatus,
    selectedLegStatus,
    manifestSearch,
    selectedManifestStatus,
    manifestTrailerIdSearch,
    asnSearch,
    selectedASNStatus,
    asnPoNumberSearch,
    ssccSearch,
    selectedSSCCStatus,
    ssccNicknameSearch,
    selectedLegDepartureDate,
    selectedLegArrivalDate,
    hideEmptyManifestTrips,
    showOnlyManifestDuplicates,
    showOnlySSCCDuplicates,
    showOnlyASNDuplicates,
    manifestDuplicateIds,
    ssccDuplicateIds,
    asnDuplicateKeys
  ]);

  useEffect(() => {
    if (hasActiveFilters) {
      return;
    }

    setFilteredIds(tripSheets.map((trip) => trip.id));
  }, [hasActiveFilters, tripSheets]);

  useEffect(() => {
    if (!workerReady) return;
    if (!hasActiveFilters) return;

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(postToWorker, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [postToWorker, workerReady, hasActiveFilters]);

  return useMemo(
    () => filteredIds.map((id) => tripSheetById.get(id)).filter((t): t is TripSheetProps => Boolean(t)),
    [filteredIds, tripSheetById]
  );
}
